import { PromisePool } from "@supercharge/promise-pool";
import retry from "async-retry";
import { ChunkingUploader } from "./chunkingUploader.js";
import Crypto from "crypto";
import { ArweaveSigner } from "arbundles";
import base64url from "base64url";
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export const CHUNKING_THRESHOLD = 50_000_000;
// eslint-disable-next-line @typescript-eslint/naming-convention
export default class Uploader {
    api;
    currency;
    currencyConfig;
    utils;
    contentTypeOverride;
    forceUseChunking;
    arbundles;
    constructor(api, utils, currency, currencyConfig) {
        this.api = api;
        this.currency = currency;
        this.currencyConfig = currencyConfig;
        this.arbundles = this.currencyConfig.bundlr.arbundles;
        this.utils = utils;
    }
    async uploadTransaction(transaction, opts) {
        let res;
        const isDataItem = this.arbundles.DataItem.isDataItem(transaction);
        if (this.forceUseChunking || (isDataItem && transaction.getRaw().length >= CHUNKING_THRESHOLD) || !isDataItem) {
            res = await this.chunkedUploader.uploadTransaction(isDataItem ? transaction.getRaw() : transaction, opts);
        }
        else {
            const { protocol, host, port, timeout, headers: confHeaders } = this.api.getConfig();
            const headers = { "Content-Type": "application/octet-stream", ...confHeaders };
            if (opts?.getReceiptSignature === true)
                headers["x-proof-type"] = "receipt";
            res = await this.api.post(`${protocol}://${host}:${port}/tx/${this.currency}`, transaction.getRaw(), {
                headers: headers,
                timeout,
                maxBodyLength: Infinity,
            });
            if (res.status === 201) {
                if (opts?.getReceiptSignature === true) {
                    throw new Error(res.data);
                }
                res.data = { id: transaction.id };
            }
        }
        switch (res.status) {
            case 402:
                throw new Error("Not enough funds to send data");
            default:
                if (res.status >= 400) {
                    throw new Error(`whilst uploading Bundlr transaction: ${res.status} ${res.statusText}`);
                }
        }
        if (opts?.getReceiptSignature) {
            res.data.verify = async () => this.utils.verifyReceipt(res.data);
        }
        return res;
    }
    async uploadData(data, opts) {
        if (typeof data === "string") {
            data = Buffer.from(data);
        }
        if (Buffer.isBuffer(data)) {
            if (data.length <= CHUNKING_THRESHOLD) {
                const dataItem = this.arbundles.createData(data, this.currencyConfig.getSigner(), {
                    ...opts,
                    anchor: opts?.anchor ?? Crypto.randomBytes(32).toString("base64").slice(0, 32),
                });
                await dataItem.sign(this.currencyConfig.getSigner());
                return (await this.uploadTransaction(dataItem, { ...opts?.upload })).data;
            }
        }
        return (await this.chunkedUploader.uploadData(data, opts)).data;
    }
    // concurrently uploads transactions
    async concurrentUploader(data, concurrency = 5, resultProcessor, logFunction) {
        const errors = [];
        const logFn = logFunction
            ? logFunction
            : async (_) => {
                return;
            };
        const results = (await PromisePool.for(data)
            .withConcurrency(concurrency >= 1 ? concurrency : 5)
            .handleError(async (error, _) => {
            errors.push(error);
            if (error.message === "Not enough funds to send data") {
                throw error;
            }
        })
            .process(async (item, i, _) => {
            await retry(async (bail) => {
                try {
                    const res = await this.processItem(item);
                    if (i % concurrency == 0) {
                        await logFn(`Processed ${i} Items`);
                    }
                    if (resultProcessor) {
                        return await resultProcessor({ item, res, i });
                    }
                    else {
                        return { item, res, i };
                    }
                }
                catch (e) {
                    if (e?.message === "Not enough funds to send data") {
                        bail(e);
                    }
                    throw e;
                }
            }, { retries: 3, minTimeout: 1000, maxTimeout: 10_000 });
        }));
        return { errors, results: results.results };
    }
    async processItem(data, opts) {
        if (this.arbundles.DataItem.isDataItem(data)) {
            return this.uploadTransaction(data, { ...opts?.upload });
        }
        return this.uploadData(data, opts);
    }
    /**
     * geneates a manifest JSON object
     * @param config.items mapping of logical paths to item IDs
     * @param config.indexFile optional logical path of the index file for the manifest
     * @returns
     */
    async generateManifest(config) {
        const { items, indexFile } = config;
        const manifest = {
            manifest: "arweave/paths",
            version: "0.1.0",
            paths: {},
        };
        if (indexFile) {
            if (!items.has(indexFile)) {
                throw new Error(`Unable to access item: ${indexFile}`);
            }
            manifest.index = { path: indexFile };
        }
        for (const [k, v] of items.entries()) {
            // @ts-expect-error constant index type
            manifest.paths[k] = { id: v };
        }
        return manifest;
    }
    get chunkedUploader() {
        return new ChunkingUploader(this.currencyConfig, this.api);
    }
    set useChunking(state) {
        if (typeof state === "boolean") {
            this.forceUseChunking = state;
        }
    }
    set contentType(type) {
        // const fullType = mime.contentType(type)
        // if(!fullType){
        //     throw new Error("Invali")
        // }
        this.contentTypeOverride = type;
    }
    async uploadBundle(transactions, opts) {
        const throwawayKey = opts?.throwawayKey ?? (await this.arbundles.getCryptoDriver().generateJWK());
        const ephemeralSigner = new ArweaveSigner(throwawayKey);
        const txs = transactions.map((tx) => (this.arbundles.DataItem.isDataItem(tx) ? tx : this.arbundles.createData(tx, ephemeralSigner)));
        const bundle = await this.arbundles.bundleAndSignData(txs, ephemeralSigner);
        // upload bundle with bundle specific tags, use actual signer for this.
        const tx = this.arbundles.createData(bundle.getRaw(), this.currencyConfig.getSigner(), {
            tags: [
                { name: "Bundle-Format", value: "binary" },
                { name: "Bundle-Version", value: "2.0.0" },
            ],
        });
        await tx.sign(this.currencyConfig.getSigner());
        const res = await this.uploadTransaction(tx, opts);
        const throwawayKeyAddress = base64url(Buffer.from(await this.arbundles.getCryptoDriver().hash(base64url.toBuffer(base64url(ephemeralSigner.publicKey)))));
        return { ...res, txs, throwawayKey, throwawayKeyAddress };
    }
}
//# sourceMappingURL=upload.js.map